import React, {useEffect, useMemo, useState} from "react";
import {Fade, Paper} from "@mui/material";
import {Col, Row} from "reactstrap";
import Button from "../../../../../../components/base/button/button";
import classnames from "classnames";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import LargeThemeColor from "../theme-management-color-components/large";
import MediumSecondaryColor from "../theme-management-color-components/medium/secondary-color";
import MediumTernaryColor from "../theme-management-color-components/medium/ternary-color";
import SmallThemeColor from "../theme-management-color-components/small";
import Api from "../../../../../../../core/services/api/api";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";


const mainColorNames = ['redLogo', 'red', 'blue'];

const UserPlatformPhoneThemeManagementView = () => {
    const [loading, setLoading] = useState(true);
    const [settingAsDefault, setSettingAsDefault] = useState(false);
    const [themes, setThemes] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState(null);
    const isMounted = useIsMounted();
    const currentlyAppliedTheme = useMemo(() => themes?.find(e => e.isCurrentlyApplied), [themes])

    const {configureNotebookTags} = useConfigureNotebook('user-platform-phone-theme-management-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.userPlatformThemeManagementPhone);
        configureBehavioralTool((tags) => tags.userPlatformThemeManagementPhone);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * With initial render of the component:
     * - Calls the function for getting the themes in the system
     */
    useEffect(() => {
        getThemes().then();
    }, [])

    /**
     * Calls the api for getting the current available themes in the system
     * Sets the [themes] in the state with the result of api
     * @return {Promise<void>}
     */
    const getThemes = async () => {
        const response = await Api.getColorThemeList();
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setThemes(response?.data);
            setSelectedThemeId(response?.data.find(e => e.isCurrentlyApplied)?.id);
        }
        setLoading(false);
    }

    /**
     * Handles selecting a theme:
     * sets the [selectedTheme] in the state to the object in the [themes] with matching id
     * @param {number} id
     */
    const onThemeSelected = (id) => {
        setSelectedThemeId(id);
    }

    /**
     * Handles the click on the apply this theme:
     * Opens a dialog for getting the confirmation from user
     * if user confirmed the action, calls function for the api call
     */
    const onApplyThisThemeClicked = () => {
        window.partner?.openConfirmationDialog(<div className={'px-3 py-4'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to apply this theme to your mobile app? It may take about 24 hours to be applied for your
                    users.
                </p>
            </div>,

            {
                maxWidth: 'sm'
            }, (isConfirmed) => isConfirmed && applySelectedThemeAsActive(), 'Apply', 'Cancel');
    }

    /**
     * Calls the api for applying the selected theme as the current active theme
     * if the api call was successful, changes the [theme] in the state to have the active theme
     * @return {Promise<void>}
     */
    const applySelectedThemeAsActive = async () => {
        setSettingAsDefault(true);
        const response = await Api.setColorThemeAsDefault(selectedThemeId);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setThemes(prevState => prevState.map(e => ({...e, isCurrentlyApplied: e.id === selectedThemeId})));
        }
        setSettingAsDefault(false);
    }

    return (
        <>
            <Row
                className='no-gutters w-100 align-items-start justify-content-center user-platform-theme-management-view'>
                <Paper className='paper w-100 px-3 pt-2  flex-wrap justify-content-between align-items-start'>
                    <div className={'d-flex flex-row justify-content-between align-items-center w-100 mt-4 mb-4 pb-2' +
                        ' px-3'}>
                        <p className={'view-title'}>
                            Phone Color Themes
                        </p>
                        <Fade in={!!selectedThemeId && (selectedThemeId !== currentlyAppliedTheme?.id)}>
                            <Button
                                onClick={onApplyThisThemeClicked}
                                color={'primary'}
                                variant={'contained'}
                            >
                                {
                                    settingAsDefault
                                        ? "Applying..."
                                        : "Apply this theme"
                                }
                            </Button>
                        </Fade>
                    </div>
                    {/*Theme selection*/}
                    <Col xs={12} xl={6} className={'order-1 order-xl-0'}>
                        <div className={'d-flex flex-column w-100'}>
                            {
                                loading
                                    ? <div className={'theme-management-list loading'}>
                                        <div/>
                                        <div/>
                                        <div/>
                                        <div/>
                                        <div/>
                                        <div/>
                                    </div>
                                    : themes?.map(theme => (
                                        <div
                                            key={theme?.id}
                                            className={classnames(
                                                'theme-card',
                                                {'active': theme.isCurrentlyApplied},
                                                {'selected': theme.id === selectedThemeId && !theme.isCurrentlyApplied}
                                            )}
                                            onClick={() => onThemeSelected(theme.id)}
                                        >
                                            <div className={'d-flex flex-wrap align-items-center'}>
                                                <div
                                                    className={'theme-main-color-indicator'}
                                                    style={{'--indicator-color': theme?.values?.find(color => color.colorName === mainColorNames[0])?.value}}
                                                />
                                                <p>
                                                    {theme?.title}
                                                </p>
                                            </div>
                                            <div className={'currently-active'}>
                                                {
                                                    theme?.isCurrentlyApplied &&
                                                    <p>Currently Active</p>
                                                }
                                            </div>
                                        </div>
                                    ))}
                        </div>
                    </Col>
                    <Col xs={12} xl={6} className={'pl-0 pl-lg-4 theme-colors order-0 order-xl-1 mb-4 mb-xl-0'}>
                        {/*Theme colors*/}
                        {
                            loading
                                ? <div className={'theme-management-colors loading'}>
                                    <div/>
                                    <div/>
                                    <div/>
                                </div>
                                : <div>
                                    <LargeThemeColor
                                        themes={themes}
                                        selectedThemeId={selectedThemeId}
                                        name={mainColorNames[0]}
                                    />
                                    <div className={'d-flex flex-wrap'}>
                                        <MediumSecondaryColor
                                            themes={themes}
                                            selectedThemeId={selectedThemeId}
                                            name={mainColorNames[1]}
                                        />
                                        <MediumTernaryColor
                                            themes={themes}
                                            selectedThemeId={selectedThemeId}
                                            name={mainColorNames[2]}
                                        />
                                    </div>
                                    <div className={'d-flex flex-row pt-3'}>
                                        {
                                            themes
                                                ?.find(e => e.id === selectedThemeId)?.values
                                                ?.filter(d => !mainColorNames.includes(d.colorName))
                                                ?.map((color, index, array) => (
                                                    array?.length === 0
                                                        ? <div key={`${color.colorName}-${color.value}`}/>
                                                        : <SmallThemeColor
                                                            key={`${color.colorName}-${color.value}`}
                                                            color={color}
                                                            array={array}
                                                        />
                                                ))
                                        }
                                    </div>
                                </div>
                        }
                    </Col>
                </Paper>
            </Row>
        </>
    );
}

export default UserPlatformPhoneThemeManagementView;
